import { IonPage } from "@ionic/react";
import { Button, Stack } from "@mui/material";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { useGenerateDeepLink } from "@src/appV2/DeepLinks";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { AppBarHeader, BackButtonLink, PageWithHeader, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { ACCOUNTS_BASE_PATH } from "@src/appV2/redesign/Accounts/paths";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { generateStripeReturnUrl } from "@src/appV2/Stripe/generateStripeReturnUrl";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation } from "@tanstack/react-query";

import { generatePaymentServiceOnboardingLink } from "./api";

export function StripeSetupPage() {
  const { showErrorToast } = useToast();

  const worker = useDefinedWorker();

  const stripeReturnLinkForceRedirect = useCbhFlag(
    CbhFeatureFlag.STRIPE_RETURN_LINK_FORCE_REDIRECT,
    {
      defaultValue: false,
    }
  );

  const { mutateAsync: generateDeepLink, isLoading: isGeneratingDeepLink } = useGenerateDeepLink();

  const { mutateAsync: generateStripeLink, isLoading: generateStripeLinkIsLoading } = useMutation({
    mutationFn: async (agentId: string) => {
      const redirectUrl = await generateStripeReturnUrl({
        flow: "stripe-setup-page",
        generateDeepLink,
        link: `${environmentConfig.REACT_APP_WEB_APP_URL}${DeprecatedGlobalAppV1Paths.OPEN_SHIFTS}`,
        purpose: "return-url",
        skipPreview: stripeReturnLinkForceRedirect,
      });

      return await generatePaymentServiceOnboardingLink(agentId, {
        refreshUrl: redirectUrl,
        returnUrl: redirectUrl,
      });
    },
  });

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_STRIPE_SETUP);

  return (
    <IonPage>
      <PageWithHeader
        appBarHeader={
          <AppBarHeader
            title="Setup Stripe"
            leftCta={<BackButtonLink alwaysUseDefaultBackTo defaultBackTo={ACCOUNTS_BASE_PATH} />}
          />
        }
        footer={
          <Stack>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={generateStripeLinkIsLoading || isGeneratingDeepLink}
              onClick={async () => {
                try {
                  logEvent(APP_V2_USER_EVENTS.STARTED_STRIPE_SETUP, {
                    hcpID: worker.userId,
                  });

                  const linkToStripe = await generateStripeLink(worker.userId);

                  await openInBrowser({ url: linkToStripe });
                  // FIXME: Automatically disabled during ESLint config update
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (error) {
                  showErrorToast(
                    "There was a problem while generating your Stripe sign-in link. Please try again"
                  );
                }
              }}
            >
              Set up your account
            </Button>
          </Stack>
        }
      >
        <Stack spacing={1}>
          <Title bold variant="h2" component="h2">
            Let's connect your account so you can get paid
          </Title>
          <Text>We partner with Stripe to issue payments. Click below to set up your account.</Text>
          <Text sx={{ color: (theme) => theme.palette.primary.light }} bold>
            All debit cards and most bank account types are eligible for instant payouts!
          </Text>
        </Stack>
      </PageWithHeader>
    </IonPage>
  );
}
